import { useState } from "react";
import FormSectionForm from "./FormSectionForm";
import ViewFormSection from "./ViewFormSection";
import {
  Tag,
  Space,
  Button,
  Row,
  Col,
  Checkbox,
  notification,
  Typography,
  Tabs
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import AddFormQuestionInFormSectionModal from "./AddFormQuestionInFormSectionModal";
import {
  useGetFormSectionsQuery,
  useCreateFormSectionMutation,
  useUpdateFormSectionMutation,
  useLazyGetFormSectionQuery
} from "../../redux/services/formSectionApi";
import { useCreateFormQuestionMutation } from "../../redux/services/formQuestionApi";
import ErrorDetails from "../../components/ErrorDetails";
import TopScrollTable from "../../components/TopScrollTable";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { humanize } from "../../utils";

const { Text } = Typography;
interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const FormSection = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isOpen, setOpen] = useState(false);
  const [isViewMode, setViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFormSection, setSelectedFormSection] = useState<any>({});
  const [getSection, sectionResponse] = useLazyGetFormSectionQuery();

  const [queryParams, setQueryParams] = useState<any>({ include_all: true, page: 1, page_size: 20 });

  const { data, refetch, isFetching } = useGetFormSectionsQuery({ ...queryParams });
  const formSections = data?.form_sections || [];

  const [createFormSection] = useCreateFormSectionMutation();
  const [updateFormSection] = useUpdateFormSectionMutation();
  const [createFormQuestion] = useCreateFormQuestionMutation();

  const onShow = (location: any) => {
    setSelectedFormSection(location);
    setViewMode(!isViewMode);
  };

  const onUpdate = (location: any) => {
    setSelectedFormSection(location);
    setIsEditMode(!isEditMode);
    setOpen(!isOpen);
    getSection(location.id);
  };

  const formSectionBrands = sectionResponse?.data?.form_section?.section_brands || [];

  const onShowAddLocationModal = (location: any) => {
    setShowModal(true);
    setSelectedFormSection(location);
  };

  const onGoBack = () => {
    setSelectedFormSection({});
    setOpen(false);
    setViewMode(false);
    setShowModal(false);
  };

  const addFormSection = async (values: object, setLoading: any, formSection: any) => {
    try {
      setLoading(true);
      await createFormSection(values).unwrap();
      api.success({
        message: "Create section successful",
        placement: "top"
      });
      formSection.resetFields();
      setLoading(false);
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create section failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateFormSection = async (
    values: object,
    setLoading: any,
    formSection: any,
    id: any
  ) => {
    try {
      setLoading(true);
      await updateFormSection({ id, formSection: values }).unwrap();
      api.success({
        message: "Update section successful",
        placement: "top"
      });
      setLoading(false);
      setIsEditMode(false);
      formSection.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update section failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const addFormQuestion = async (values: object, setLoading: any, formSection: any) => {
    try {
      setLoading(true);
      await createFormQuestion(values).unwrap();
      api.success({
        message: "Create question successful",
        placement: "top"
      });
      formSection.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create question failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Type",
      dataIndex: "section_type",
      key: "section_type",
      render: (section_type) => <>{humanize(section_type)}</>
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, location) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => onShow(location)} title="View"></Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => onUpdate(location)}
            title="Update"
          ></Button>
          <Button
            icon={<PlusCircleOutlined />}
            onClick={() => onShowAddLocationModal(location)}
            title="Add Question"
          ></Button>
        </Space>
      )
    }
  ];

  const onTabChange = (key: string) => {
    const values: any = { section_type: key, page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  const pagination = {
    pageSize: queryParams?.page_size,
    current: queryParams?.page,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onShowSizeChange: (current: any, page_size: number) => {
      const values: any = { page_size: page_size };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    },
    onChange: (page: number) => {
      const values: any = { page: page };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const refetchWithReset = () => {
    const values: any = { page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    refetch();
  };

  const onChangeActiveOnly = (e: CheckboxChangeEvent) => {
    const include_all: boolean = e.target.checked;
    const values: any = { include_all: !include_all };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  return (
    <>
      {contextHolder}
      {!isViewMode && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
              <div>
                <h2>Sections</h2>
                <Text>Total sections: {data?.meta?.total_count || 0}</Text>
              </div>
              <div style={{ display: "flex", gap: 3 }}>
                <Button onClick={() => setOpen(!isOpen)} size="large" type="primary">
                  Create Section
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: "",
                    label: "All"
                  },
                  {
                    key: "pre_survey",
                    label: "Pre Survey"
                  },
                  {
                    key: "in_survey",
                    label: "In Survey"
                  },
                  {
                    key: "post_survey",
                    label: "Post Survey"
                  },
                  {
                    key: "pre_visit",
                    label: "Pre Visit"
                  },
                  {
                    key: "in_visit",
                    label: "In Visit"
                  },
                  {
                    key: "post_visit",
                    label: "Post Visit"
                  }
                ]}
                onChange={onTabChange}
              />
            </Col>
          </Row>
          <Row style={{ marginBottom: 20 }}>
            <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
              <Checkbox onChange={onChangeActiveOnly}>Show Active Only</Checkbox>
            </Col>
          </Row>
          <TopScrollTable
            rowKey="id"
            columns={columns}
            dataSource={formSections}
            pagination={pagination}
            bordered
            loading={isFetching}
            scroll={{ x: 400 }}
          />
        </>
      )}

      {isViewMode && (
        <ViewFormSection formSectionId={selectedFormSection?.id} onGoBack={onGoBack} />
      )}

      {isOpen && (
        <FormSectionForm
          setOpen={setOpen}
          isOpen={true}
          isEditMode={isEditMode}
          addFormSection={addFormSection}
          setIsEditMode={setIsEditMode}
          onUpdateFormSection={onUpdateFormSection}
          selectedFormSection={selectedFormSection}
          formSectionBrands={formSectionBrands}
          setSelectedFormSection={setSelectedFormSection}
        />
      )}

      <AddFormQuestionInFormSectionModal
        isModalOpen={showModal}
        formSection={selectedFormSection}
        onCancel={onGoBack}
        isEditMode={false}
        addFormQuestion={addFormQuestion}
        selectedFormQuestion={{}}
      />
    </>
  );
};

export default FormSection;
